import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Link2, Wand2, CheckCircle2, Loader2, Store, ArrowRight, Sparkles, Image, DollarSign, LayoutGrid } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

const STEPS = [
  { icon: Link2, label: 'Fetching your bio link' },
  { icon: Image, label: 'Importing images & branding' },
  { icon: LayoutGrid, label: 'Rebuilding your product blocks' },
  { icon: DollarSign, label: 'Detecting pricing & offers' },
  { icon: Sparkles, label: 'Pre-configuring your AI Concierge' },
];

export default function Onboarding() {
  const navigate = useNavigate();
  const [url, setUrl] = useState('');
  const [phase, setPhase] = useState('input'); // input | parsing | done
  const [current, setCurrent] = useState(0);
  const timers = useRef([]);

  useEffect(() => () => timers.current.forEach(clearTimeout), []);

  const start = () => {
    if (!url.trim()) { setUrl('biocloser.com/creator'); }
    setPhase('parsing');
    setCurrent(0);
    STEPS.forEach((_, i) => {
      timers.current.push(setTimeout(() => setCurrent(i + 1), (i + 1) * 1000));
    });
    timers.current.push(setTimeout(() => setPhase('done'), STEPS.length * 1000 + 300));
  };

  return (
    <div className="luxe-bg flex min-h-screen w-full items-center justify-center px-4 py-10">
      <div className="w-full max-w-md">
        <div className="mb-6 flex items-center justify-center gap-2">
          <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary text-primary-foreground"><Store className="h-5 w-5" /></span>
          <span className="font-display text-xl font-bold text-white">BioCloser</span>
        </div>

        {phase === 'input' && (
          <div className="glass animate-fade-up rounded-3xl p-7">
            <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/15">
              <Wand2 className="h-6 w-6 text-primary" />
            </div>
            <h1 className="font-display text-2xl font-bold text-white">Instant Auto-Import</h1>
            <p className="mt-2 text-sm text-white/70">
              Paste your existing bio link and we'll rebuild your entire storefront — products, images, pricing & a pre-trained AI Concierge — in about 5 seconds.
            </p>

            <div className="mt-5">
              <div className="relative">
                <Link2 className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder="biocloser.com/you or linktr.ee/you"
                  className="rounded-xl border-white/10 bg-white/[0.04] pl-9 focus-visible:ring-primary"
                  onKeyDown={(e) => e.key === 'Enter' && start()}
                />
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                {['biocloser.com', 'linktr.ee', 'beacons.ai'].map((s) => (
                  <span key={s} className="rounded-full border border-white/10 bg-white/[0.03] px-2.5 py-1 text-xs text-muted-foreground">{s}</span>
                ))}
              </div>
              <Button onClick={start} className="mt-5 h-12 w-full rounded-xl bg-primary text-base font-bold text-primary-foreground hover:bg-primary/90 glow-lime">
                Import my store <ArrowRight className="ml-1.5 h-4 w-4" />
              </Button>
              <button onClick={() => navigate('/dashboard')} className="mt-3 w-full text-center text-xs text-muted-foreground hover:text-white">
                Skip — start from scratch
              </button>
            </div>
          </div>
        )}

        {phase === 'parsing' && (
          <div className="glass animate-fade-up rounded-3xl p-7">
            <div className="mb-5 flex items-center gap-3">
              <Loader2 className="h-5 w-5 animate-spin text-primary" />
              <div>
                <p className="font-display text-sm font-semibold text-white">Building your store…</p>
                <p className="truncate text-xs text-muted-foreground">{url || 'biocloser.com/creator'}</p>
              </div>
            </div>
            <div className="space-y-2.5">
              {STEPS.map((s, i) => {
                const state = i < current ? 'done' : i === current ? 'active' : 'wait';
                return (
                  <div key={i} className={`flex items-center gap-3 rounded-xl border p-3 transition-luxe ${state === 'done' ? 'border-primary/30 bg-primary/[0.06]' : state === 'active' ? 'border-white/15 bg-white/[0.04]' : 'border-white/5 bg-transparent opacity-50'}`}>
                    <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-white/[0.06]">
                      {state === 'done' ? <CheckCircle2 className="h-4 w-4 text-primary" /> : state === 'active' ? <Loader2 className="h-4 w-4 animate-spin text-primary" /> : <s.icon className="h-4 w-4 text-muted-foreground" />}
                    </span>
                    <span className={`text-sm ${state === 'wait' ? 'text-muted-foreground' : 'text-white'}`}>{s.label}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {phase === 'done' && (
          <div className="glass animate-fade-up rounded-3xl p-7 text-center">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-primary/15">
              <CheckCircle2 className="h-9 w-9 text-primary" />
            </div>
            <h1 className="font-display text-2xl font-bold text-white">Your store is ready! 🎉</h1>
            <p className="mt-2 text-sm text-white/70">We imported 6 products, your branding, and pre-trained your AI Concierge to handle objections.</p>
            <div className="mt-5 grid grid-cols-3 gap-2 text-center">
              {[['6', 'Products'], ['12', 'Images'], ['1', 'AI Concierge']].map(([n, l]) => (
                <div key={l} className="rounded-xl border border-white/10 bg-white/[0.03] p-3">
                  <p className="font-display text-lg font-bold text-white">{n}</p>
                  <p className="text-xs text-muted-foreground">{l}</p>
                </div>
              ))}
            </div>
            <div className="mt-6 space-y-2">
              <Button onClick={() => navigate('/store')} className="h-12 w-full rounded-xl bg-primary font-bold text-primary-foreground hover:bg-primary/90 glow-lime">
                View my storefront <ArrowRight className="ml-1.5 h-4 w-4" />
              </Button>
              <Button onClick={() => navigate('/dashboard')} variant="ghost" className="w-full rounded-xl text-white/80 hover:bg-white/10 hover:text-white">
                Go to dashboard
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
